import { useState, useCallback, useRef } from "react";
import { useApi } from "../callApi/callApi";
import { User } from "../../types/user";

interface AvatarCache {
  [email: string]: {
    avatarUrl: string | undefined;
    timestamp: number;
    loading: boolean;
  };
}

interface UserCache {
  [email: string]: {
    user: User;
    timestamp: number;
    loading: boolean;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook for efficient user data management with caching
 * This helps minimize backend calls when multiple components need the same user data
 */
export const useUsers = () => {
  const [userCache, setUserCache] = useState<UserCache>({});
  const [avatarCache, setAvatarCache] = useState<AvatarCache>({});
  const userCacheRef = useRef<UserCache>({});
  const avatarCacheRef = useRef<AvatarCache>({});
  const callApi = useApi();

  // Keep refs in sync with state
  userCacheRef.current = userCache;
  avatarCacheRef.current = avatarCache;

  // Ensure a user exists in firestore.
  const createUser = useCallback(async (): Promise<boolean> => {
    const response = await callApi(
      `users`,
      { method: "POST" },
      "openid email profile"
    );
    if (response.ok) {
      return true;
    }
    return false;
  }, [callApi]);

  // Get user data with caching
  const getUser = useCallback(
    async (email: string): Promise<User | null> => {
      if (!email) return null;

      const now = Date.now();
      const cached = userCacheRef.current[email];

      // Return cached data if it's still valid
      if (
        cached &&
        now - cached.timestamp < CACHE_DURATION &&
        !cached.loading
      ) {
        return cached.user;
      }

      // Set loading state
      setUserCache((prev) => ({
        ...prev,
        [email]: { ...prev[email], loading: true },
      }));

      try {
        const response = await callApi(
          `users/${email}`,
          { method: "GET" },
          "openid email profile"
        );

        if (response.ok) {
          const user: User = await response.json();

          // Cache the result
          setUserCache((prev) => ({
            ...prev,
            [email]: {
              user,
              timestamp: now,
              loading: false,
            },
          }));

          return user;
        } else {
          // User not found, create a default user object
          const defaultUser: User = {
            email: email,
            displayName: email,
          };

          // Cache the default result
          setUserCache((prev) => ({
            ...prev,
            [email]: {
              user: defaultUser,
              timestamp: now,
              loading: false,
            },
          }));

          return defaultUser;
        }
      } catch (error) {
        console.error(`Error fetching user ${email}:`, error);

        // On error, create a default user object
        const defaultUser: User = {
          email: email,
          displayName: email.split("@")[0], // Use email prefix as display name
        };

        // Cache the default result
        setUserCache((prev) => ({
          ...prev,
          [email]: {
            user: defaultUser,
            timestamp: now,
            loading: false,
          },
        }));

        return defaultUser;
      }
    },
    [callApi] // Remove userCache from dependencies
  );

  const updateAvatar = useCallback(
    async (
      email: string,
      blob: Blob,
      imageFile: string
    ): Promise<boolean | Error> => {
      const formData = new FormData();
      formData.append("image", blob, imageFile);
      const response = await callApi(
        `users/${email}/avatar`,
        {
          method: "PUT",
          body: formData,
        },
        "openid email profile"
      );
      if (response.ok) {
        return true;
      }
      return new Error(`Failed to update avatar for ${email}`);
    },
    [callApi]
  );

  const updateUsername = useCallback(
    async (email: string, newUsername: string): Promise<boolean | Error> => {
      const response = await callApi(
        `users/${email}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            displayName: newUsername.trim(),
          }),
        },
        "openid email profile"
      );
      if (response.ok) {
        return true;
      }
      return new Error(`Failed to update user ${email}`);
    },
    [callApi]
  );

  const updateEmailPreferences = useCallback(
    async (
      email: string,
      preferences: { [key: string]: boolean }
    ): Promise<boolean | Error> => {
      const response = await callApi(
        `users/${email}/preferences`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(preferences),
        },
        "openid email profile"
      );
      if (response.ok) {
        return true;
      }
      return new Error(`Failed to update email preferences for ${email}`);
    },
    [callApi]
  );

  // Get multiple users efficiently
  const getUsers = useCallback(
    async (emails: string[]): Promise<User[]> => {
      if (!emails || emails.length === 0) return [];

      const uniqueEmails = Array.from(new Set(emails)).filter(Boolean);

      // Use Promise.all to fetch all users concurrently
      const promises = uniqueEmails.map(async (email) => {
        const user = await getUser(email);
        return user;
      });

      const users = await Promise.all(promises);
      return users.filter((user): user is User => user !== null);
    },
    [getUser]
  );

  // Preload users (useful for components that know they'll need certain users)
  const preloadUsers = useCallback(
    (emails: string[]) => {
      emails.forEach((email) => {
        if (email && !userCacheRef.current[email]) {
          getUser(email);
        }
      });
    },
    [getUser]
  );

  // Clear cache
  const clearCache = useCallback(() => {
    setUserCache({});
  }, []);

  // Get cached users
  const getCachedUsers = useCallback(
    (emails: string[]): User[] => {
      return emails
        .map((email) => userCache[email]?.user)
        .filter((user): user is User => user !== undefined);
    },
    [userCache]
  );

  // Check if users are loading
  const areUsersLoading = useCallback(
    (emails: string[]): boolean => {
      return emails.some((email) => userCache[email]?.loading);
    },
    [userCache]
  );

  // Get avatar URL with caching
  const getAvatar = useCallback(
    async (
      email: string,
      auth0Picture?: string
    ): Promise<string | undefined> => {
      if (!email) return auth0Picture;

      const now = Date.now();
      const cached = avatarCacheRef.current[email];

      // Return cached data if it's still valid
      if (
        cached &&
        now - cached.timestamp < CACHE_DURATION &&
        !cached.loading
      ) {
        return cached.avatarUrl;
      }

      // Set loading state
      setAvatarCache((prev) => ({
        ...prev,
        [email]: { ...prev[email], loading: true },
      }));

      try {
        const response = await callApi(
          `users/${email}/avatar`,
          { method: "GET" },
          "openid email profile"
        );

        let avatarUrl: string | undefined;
        // Server gives 204 on no avatars found
        if (response.status !== 204) {
          const data = await response.json();
          avatarUrl = data.avatarUrl;
        } else {
          avatarUrl = auth0Picture;
        }

        // Cache the result
        setAvatarCache((prev) => ({
          ...prev,
          [email]: {
            avatarUrl,
            timestamp: now,
            loading: false,
          },
        }));

        return avatarUrl;
      } catch (error) {
        console.error(`Error fetching avatar for ${email}:`, error);

        // On error, use auth0 picture as fallback
        const fallbackUrl = auth0Picture;

        // Cache the fallback result
        setAvatarCache((prev) => ({
          ...prev,
          [email]: {
            avatarUrl: fallbackUrl,
            timestamp: now,
            loading: false,
          },
        }));

        return fallbackUrl;
      }
    },
    [callApi]
  );

  // Get multiple avatars efficiently
  const getAvatars = useCallback(
    async (
      emails: string[],
      auth0Pictures?: { [email: string]: string }
    ): Promise<{ [email: string]: string | undefined }> => {
      if (!emails || emails.length === 0) return {};

      const uniqueEmails = Array.from(new Set(emails)).filter(Boolean);

      // Use Promise.all to fetch all avatars concurrently
      const promises = uniqueEmails.map(async (email) => {
        const avatarUrl = await getAvatar(email, auth0Pictures?.[email]);
        return { email, avatarUrl };
      });

      const results = await Promise.all(promises);
      return results.reduce((acc, { email, avatarUrl }) => {
        acc[email] = avatarUrl;
        return acc;
      }, {} as { [email: string]: string | undefined });
    },
    [getAvatar]
  );

  // Preload avatars (useful for components that know they'll need certain avatars)
  const preloadAvatars = useCallback(
    (emails: string[], auth0Pictures?: { [email: string]: string }) => {
      emails.forEach((email) => {
        if (email && !avatarCacheRef.current[email]) {
          getAvatar(email, auth0Pictures?.[email]);
        }
      });
    },
    [getAvatar]
  );

  // Get cached avatar
  const getCachedAvatar = useCallback(
    (email: string): string | undefined => {
      return avatarCache[email]?.avatarUrl;
    },
    [avatarCache]
  );

  // Check if avatar is loading
  const isAvatarLoading = useCallback(
    (email: string): boolean => {
      return avatarCache[email]?.loading || false;
    },
    [avatarCache]
  );

  // Clear avatar cache
  const clearAvatarCache = useCallback(() => {
    setAvatarCache({});
  }, []);

  return {
    createUser,
    getUser,
    updateAvatar,
    updateUsername,
    updateEmailPreferences,
    getUsers,
    preloadUsers,
    clearCache,
    getCachedUsers,
    areUsersLoading,
    userCache,
    getAvatar,
    getAvatars,
    preloadAvatars,
    getCachedAvatar,
    isAvatarLoading,
    clearAvatarCache,
  };
};

export default useUsers;
